import api from './api';
import {CHANNELS_SET_ACTIVE} from './channels';

import NetworkError from '../utils/error/NetworkError';

export const TAXONS_SET_ROOT_TAXON = 'TAXONS_SET_ROOT_TAXON';
export const TAXONS_SET_ROOT_TAXON_CHILDREN = 'TAXONS_SET_ROOT_TAXON_CHILDREN';

export const TAXONS_SET_TAXON = 'TAXONS_SET_TAXON';
export const TAXONS_SET_CHILDREN = 'TAXONS_SET_CHILDREN';

export const TAXONS_FETCH_PRODUCTS = 'TAXONS_FETCH_PRODUCTS';
export const TAXONS_SET_PRODUCTS = 'TAXONS_SET_PRODUCTS';
export const TAXONS_ERROR_PRODUCTS = 'TAXONS_ERROR_PRODUCTS';

const PRODUCTS_LIMIT = 18;

const initialState = {
  root: {
    taxon: undefined,
    children: undefined,
    subchildren: undefined
  },
  taxon: {},
  children: {},
  products: {
    all: {},
    limited: {},
    popular: {}
  }
};

export default function reducer(state = initialState, {type, payload}) {
  switch (type) {
    case TAXONS_SET_ROOT_TAXON:
      return {
        ...state,
        root: {
          ...state.root,
          taxon: payload
        }
      };
    case TAXONS_SET_ROOT_TAXON_CHILDREN:
      return {
        ...state,
        root: {
          ...state.root,
          children: payload
        }
      };
    case TAXONS_SET_TAXON:
      return {
        ...state,
        taxon: {
          ...state.taxon,
          [payload.slug]: payload.taxon
        }
      };
    case TAXONS_SET_CHILDREN:
      return {
        ...state,
        children: {
          ...state.children,
          [payload.slug]: payload.children
        }
      };
    case TAXONS_FETCH_PRODUCTS:
      return {
        ...state,
        products: {
          ...state.products,
          [payload.type]: {
            ...state.products[payload.type],
            [payload.slug]: {
              ...(state.products[payload.type][payload.slug] || {total: -1, loaded: 0, items: []}),
              isLoading: true,
              error: false,
              page: payload.page
            }
          }
        }
      };
    case TAXONS_SET_PRODUCTS:
      return {
        ...state,
        products: {
          ...state.products,
          [payload.type]: {
            ...state.products[payload.type],
            [payload.slug]: {
              ...state.products[payload.type][payload.slug],
              isLoading: false,
              error: false,
              page: payload.page,
              total: payload.total,
              loaded: state.products[payload.type][payload.slug].loaded + payload.items.length,
              items: state.products[payload.type][payload.slug].items.concat(payload.items)
            }
          }
        }
      };
    case TAXONS_ERROR_PRODUCTS:
      return {
        ...state,
        products: {
          ...state.products,
          [payload.type]: {
            ...state.products[payload.type],
            [payload.slug]: {
              ...(state.products[payload.type][payload.slug] || {}),
              isLoading: false,
              error: true,
              page: Math.min(1, payload.page - 1)
            }
          }
        }
      };
    case CHANNELS_SET_ACTIVE:
      return initialState;
    default:
      return state;
  }
}

export function setTaxon(slug, taxon) {
  return {type: TAXONS_SET_TAXON, payload: {slug, taxon}}
}

export function setTaxonChildren(slug, children) {
  return {type: TAXONS_SET_CHILDREN, payload: {slug, children}};
}

export function fetchRootTaxon() {
  return dispatch => {
    dispatch({type: TAXONS_SET_ROOT_TAXON, payload: null});

    return dispatch(api.get(`taxons/by-code/${process.env.ROOT_TAXON}`, undefined, {cache: 'default'}))
      .then(rootResponse => dispatch({type: TAXONS_SET_ROOT_TAXON, payload: rootResponse.content}));
  }
}

export function fetchRootTaxonChildren() {
  return dispatch => {
    dispatch({type: TAXONS_SET_ROOT_TAXON_CHILDREN, payload: null});

    return dispatch(api.getAll(`taxons/by-code/${process.env.ROOT_TAXON}/children`, {'max-depth': 2}, {cache: 'default'}))
      .then(rootChildrenResponse => dispatch({
        type: TAXONS_SET_ROOT_TAXON_CHILDREN,
        payload: rootChildrenResponse.content.items
      }));
  }
}

export function fetchTaxonBySlug(slug, cookies) {
  return dispatch => {
    dispatch(setTaxon(slug, null));

    return dispatch(api.get(`taxons/by-slug/${slug}`, undefined, {cache: 'default'}))
      .then(taxonResponse => {
        const taxon = taxonResponse.content;

        if (cookies && cookies.blacklist) {
          const excludedTaxons = JSON.parse(cookies.blacklist);

          const mainTaxonParentSlugs = taxon && taxon.parents.map(taxon => taxon.slug);

          if (mainTaxonParentSlugs && [...mainTaxonParentSlugs, taxon.slug].some(taxon => excludedTaxons.includes(taxon))) {
            return Promise.reject(new NetworkError(404, {code: 404, message: 'Not Found'}));
          }
        }

        dispatch(setTaxon(slug, taxon));
        return taxon;
      })
      .catch(err => {
        if (err.status === 404) {
          dispatch(setTaxon(slug, false));
        }

        throw err;
      });
  };
}

export function fetchTaxonChildrenBySlug(slug, depth = 2) {
  return dispatch => {
    dispatch(setTaxonChildren(slug, null));

    return dispatch(api.getAll(`taxons/by-slug/${slug}/children`, {'max-depth': depth}, {cache: 'default'}))
      .then(childrenResponse => {
        dispatch(setTaxonChildren(slug, childrenResponse.content.items));
        return childrenResponse.content.items;
      })
      .catch(err => {
        if (err.status === 404) {
          dispatch(setTaxonChildren(slug, false));

          return;
        }

        throw err;
      });
  };
}

const fetchTaxonProductsBySlugTypeMap = {
  all: '',
  limited: '/limited_offer',
  popular: '/popular/weekly'
};

export function fetchTaxonProductsBySlug(type, slug, page = 1, limit = PRODUCTS_LIMIT) {
  return dispatch => {
    dispatch({type: TAXONS_FETCH_PRODUCTS, payload: {type, slug, page}});

    const fetchAction = page < 0
      ? api.getAll(`taxons/by-slug/${slug}/products${fetchTaxonProductsBySlugTypeMap[type]}`, undefined, {cache: 'default'})
      : api.get(`taxons/by-slug/${slug}/products${fetchTaxonProductsBySlugTypeMap[type]}`, {limit, page}, {cache: 'default'});

    return dispatch(fetchAction)
      .then(
        productsResponse => dispatch({
          type: TAXONS_SET_PRODUCTS,
          payload: {type, slug, page, total: productsResponse.meta.total, items: productsResponse.content.items}
        }),
        productsError => {
          dispatch({
            type: TAXONS_ERROR_PRODUCTS,
            payload: {type, slug, page}
          });

          throw productsError;
        }
      );
  };
}

export function fetchTaxonProductsBySlugPosition(type, slug, page = 1, limit = PRODUCTS_LIMIT) {
  return (dispatch) => {
    dispatch({ type: TAXONS_FETCH_PRODUCTS, payload: { type, slug, page } });

    const url = `taxons/by-slug/${slug}/products/position`;

    const fetchAction =
      page < 0 ? api.getAll(url, undefined, { cache: "default" }) : api.get(url, { limit, page }, { cache: "default" });

    return dispatch(fetchAction).then(
      (productsResponse) =>
        dispatch({
          type: TAXONS_SET_PRODUCTS,
          payload: {
            type,
            slug,
            page,
            total: productsResponse.meta.total,
            items: productsResponse.content.items,
          },
        }),
      (productsError) => {
        dispatch({
          type: TAXONS_ERROR_PRODUCTS,
          payload: { type, slug, page },
        });

        throw productsError;
      },
    );
  };
}



// WEBPACK FOOTER //
// ./ducks/taxons.js